import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import moment from "moment";
import momentTz from "moment-timezone";

import { Lead } from "@/types";

import { userMe, userIsLoggedIn, getToken } from "@/store/auth";
import { leadsGetAll, updateLeadStatus, leadsChangeLSAStatus, clearLeads, leadUpdate } from "@/store/leads";

import AppPageContainer from "@/components/AppPageContainer";
import LeadsTable from "@/components/Tables/LeadsTable";
import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import { TableFilters } from "@/types/filters";
import Filters from "@/components/Filters";
import { DEFAULT_STATUSES } from "@/constants/leadStatuses";
import AppButton from "@/components/AppButton";
import AppText from "@/components/AppText";
import AppPagination from "@/components/Tables/AppPagination";

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const tz = momentTz.tz.guess();

  const { isLoggedIn, me } = useAppSelector((state) => state.auth);
  const { leads, pagination } = useAppSelector((state) => state.leads);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [filters, setFilters] = useState<TableFilters>({
    phone: null,
    type: {
      sort: null,
      value: null
    },
    date: null,
    status: null
  });
  const [localLeads, setLocalLeads] = useState(leads);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLocalLeads(leads)
  }, [leads]);

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(clearLeads());
      dispatch(userIsLoggedIn());
    } else if (!me.id) {
      dispatch(userMe());
    }
  }, [isLoggedIn, me.id, dispatch]);

  useEffect(() => {
    if (me.id) {
      dispatch(clearLeads());
      dispatch(leadsGetAll({ page: page, perPage: pagination?.per_page || 50 }));
    }
  }, [me.id, page, dispatch]);

  useEffect(() => {
    if (localLeads.length) {
      const newList = searchQuery
        ? localLeads.filter((item) => {
            const searchLower = searchQuery.toLowerCase();

            // Concatenate all string fields that might be null/undefined and compare in lowercase
            const matchesBasicFields = [
              item.type,
              item.name,
              item.phone,
            ].some((field) => (field ?? "").toLowerCase().includes(searchLower));

            const createdAtMatches = moment(item.created_at)
              .tz(tz)
              .format("D MMM YYYY HH:mm")
              .toLowerCase()
              .includes(searchLower);

            return matchesBasicFields || createdAtMatches;
          })
        : localLeads;
      // Apply filters after search
      const filteredResults = applyFilters(newList, filters);
      setFilteredLeads(filteredResults);
    } else {
      setFilteredLeads([]);
    }
  }, [localLeads, searchQuery, tz, filters]);

  const handleChangeInput = (value: string) => {
    setSearchQuery(value);
  };

  const handleClickLink = (lead: Lead) => {
    window.open(lead.shortlink);
  };

  const handleStatusChange = async (leadId: number, status: string) => {
    try {
      // Якщо статус Booked або Archived, спочатку оновлюємо LSA статус
      if (status === 'booked' || status === 'archived') {
        // Знаходимо лід за id
        const lead = leads.find(lead => lead.id === leadId);
        
        // Перевіряємо тип ліда перед зміною LSA статусу
        if (lead && (lead.type === 'Message' || lead.type === 'Answered Call')) {
          await dispatch(leadsChangeLSAStatus({
            id: leadId, 
            data: { status }
          })).unwrap();
        }
      }

      // Потім оновлюємо локальний статус
      await dispatch(updateLeadStatus({ leadId, status })).unwrap();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const applyFilters = (leads: Lead[], filters: TableFilters) => {
    let filteredLeads = [...leads];

    // Type filter
    if (filters.type.value) {
      filteredLeads = filteredLeads.filter(lead => 
        lead.type === filters.type.value
      );
    }

    // Phone filter
    if (filters.phone === 'with_phone') {
      filteredLeads = filteredLeads.filter(lead => lead.phone && lead.phone.trim() !== '');
    } else if (filters.phone === 'without_phone') {
      filteredLeads = filteredLeads.filter(lead => !lead.phone || lead.phone.trim() === '');
    }

    // Date sort
    if (filters.date) {
      filteredLeads.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return filters.date === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    // Status filter
    if (filters.status) {
      if (filters.status === 'no_status') {
        filteredLeads = filteredLeads.filter(lead => !lead.status);
      } else if (filters.status === 'custom') {
        filteredLeads = filteredLeads.filter(lead => 
          lead.status && !(DEFAULT_STATUSES as string[]).includes(lead.status.toLowerCase())
        );
      } else {
        filteredLeads = filteredLeads.filter(lead => lead.status === filters.status);
      }
    }

    return filteredLeads;
  };

  const handleFiltersChange = (newFilters: TableFilters) => {
    setFilters(newFilters);
  };

  const handleChangeSetting = async (leadId: number, field: string, currentValue: boolean) => {
    setLocalLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.id === leadId ? { ...lead, [field]: !currentValue } : lead
      )
    );
    
    try {
      // Call API in the background
      await dispatch(leadUpdate({ id: leadId, data: { [field]: !currentValue } })).unwrap();
    } catch (error) {
      console.error("Failed to update user setting:", error);

      // Rollback UI if API call fails
      setLocalLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, [field]: currentValue } : lead
        )
      );
    }
  };

  return (
    <AppPageContainer
      title="Dashboard"
      titleAdorment={
        <AppBox width={320}>
          <AppInput
            placeholder="Type to search..."
            fullWidth
            onChange={(value) => handleChangeInput(value)}
            value={searchQuery}
          />
        </AppBox>
      }
    >
      <AppBox height="95%" column gap={2}>
        <Filters 
          filters={filters}
          onChange={handleFiltersChange}
        />
        <LeadsTable
          tableRows={filteredLeads}
          onClickLink={handleClickLink}
          onStatusChange={handleStatusChange}
          onChangeSetting={handleChangeSetting}
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
        <AppPagination
          totalRows={pagination?.total || 0}
          rowsPerPage={pagination?.per_page || 50}
          currentPage={(pagination?.current_page || 1) - 1}
          onPageChange={(newPage) => dispatch(leadsGetAll({ page: newPage + 1, perPage: pagination?.per_page || 50 }))}
          onRowsPerPageChange={(newRowsPerPage) => dispatch(leadsGetAll({ page: 1, perPage: newRowsPerPage }))}
        />
      </AppBox>
    </AppPageContainer>
  );
};

export default Dashboard;